// Synced view between the main slide and the open block image
// (phase1-prototype-plan.md). Only user-level state lives here: whether sync is on,
// the WSI -> SectionStar transform, and landmark points. Per-frame viewport work is
// done by utils/viewer-sync.js and never goes through Redux.
export const DEFAULT_BLOCK_IMAGE_SYNC = {
  synced: false,
  // { k, theta, tx, ty } in image pixels; see utils/viewer-sync-math.js
  transform: null,
  // "anchor" (from the views when Sync was clicked) or "landmarks"
  transformSource: null,
  residualUm: null,
  // { ratio, used: "nominal" | "measured", nominalMpp, measuredMpp, usedMpp } from a landmark fit
  scaleCheck: null,
  pointMode: false,
  landmarks: { wsi: [], ss: [] },
};

const initialState = {
  imageValue: null,
  type: null,
  isQuorumImageLoad: false,
  annotationType: null,
  isAnnotationActive: false,
  setAnnotations: false,
  isQuorumImageInit: false,
  removedAnnotation: false,
  when: null,
  annotatedTime: null,
  annotationColor: null,
  userAnnotation: [],
  annotationData: [],
  toogleData: null,
  liveShareOn: false,
  userLiveShareOn: false,
  isSoketDataUpdate: false,
  scaleUnit: "cm", // default unit to cm
  scaleRatio: 0.01,
  liveSyncUrl: null,
  liveSyncMetaData: null,
  annotationDownloadUrl: null,
  slideUniqueId: null,
  isAnnotationUpload: null,
  liveSyncPopupload: false,
  selectedAnnotationId: null,
  annotationPopupShow: null,
  description: "",
  viewerActivityLogId: null,
  viewerActivityData: [],
  viewerDataArray: [],
  roiData: [],
  annotationDataForHighlightAreas: [],
  heatMapData: [],
  selectedSlideLabels: null,

  // SectionStar block images. selectedBlockImageIndex is the single source of truth
  // for the whole feature: the sidebar list and the panel's prev/next arrows both do
  // nothing but change this number, which is what keeps them synchronised in both
  // directions without any explicit sync code. null means the panel is closed.
  blockImages: [],
  // All three expected states in display order, each flagged available or not, so
  // the sidebar can list a missing one instead of silently omitting it. Separate
  // from blockImages, whose indices are the selection state.
  blockImageRows: [],
  blockImagesMeta: null,
  blockImagesLoading: false,
  blockImagesError: null,
  selectedBlockImageIndex: null,
  blockImageSync: DEFAULT_BLOCK_IMAGE_SYNC,
};

// `|| DEFAULT` guards a rehydrated session persisted before this key existed.
const syncOf = (state) => state.blockImageSync || DEFAULT_BLOCK_IMAGE_SYNC;

// Switching between White and ClaraSight of the same state (block / tape / slide)
// keeps the sync: both are taken from the same position, so the alignment carries
// over. utils/viewer-sync.js re-checks that the new image has the same pixel size
// before re-applying it. Any other change of image drops the sync.
const syncAfterSelecting = (state, nextIndex) => {
  const images = state.blockImages || [];
  const current = images[state.selectedBlockImageIndex];
  const next = images[nextIndex];
  return current && next && current.state && current.state === next.state
    ? syncOf(state)
    : DEFAULT_BLOCK_IMAGE_SYNC;
};

export default (state = initialState, action) => {
  switch (action.type) {
    case "SET_USER_ANNOTATION_DATA": {
      const data = action.payload;
      return {
        ...state,
        userAnnotation: data,
      };
    }

    case "SET_CURRENT_ANNOTATION_DATA": {
      const data = action.payload;
      return {
        ...state,
        annotationData: data,
      };
    }

    case "SET_DEFAULT_SCALE_UNIT": {
      return {
        ...state,
        scaleUnit: initialState.scaleUnit,
      };
    }

    case "SET_SCALE_UNIT": {
      const data = action.payload;
      return {
        ...state,
        scaleUnit: data,
      };
    }

    case "SET_DEFAULT_SCALE_RATIO": {
      return {
        ...state,
        scaleRatio: initialState.scaleRatio,
      };
    }

    case "SET_SCALE_RATIO": {
      const data = action.payload;
      return {
        ...state,
        scaleRatio: data,
      };
    }

    case "DELETE_ANNOTATION_POPUP": {
      const deletedId = action.payload;
      return {
        ...state,
        selectedAnnotationId: deletedId,
        annotationPopupShow: action.status,
      };
    }

    case "DELETE_ANNOTATION_DATA": {
      const deletedId = action.payload;
      return {
        ...state,
        annotationData: state.annotationData.filter(
          (data) => data.pk !== deletedId
        ),
      };
    }

    case "UPDATE_CURRENT_ANNOTATION_DATA": {
      const data = action.payload;
      return {
        ...state,
        annotationData: [...state.annotationData, data],
        isAnnotationUpload:
          typeof data.isAnnotationUpload !== "undefined"
            ? data.isAnnotationUpload
            : null,
      };
    }

    case "RESET_ANNOTATION_UPLOAD": {
      return {
        ...state,
        isAnnotationUpload: null,
      };
    }

    case "UPDATE_CURRENT_ANNOTATION_DATA_IN_VALIDATE": {
      const data = action.payload;
      return {
        ...state,
        annotationData: [...state.annotationData, ...data],
        isSoketDataUpdate: true,
      };
    }

    case "SET_SOCKET_DATE_ACTION_FALSE": {
      const data = action.payload;
      return {
        ...state,
        isSoketDataUpdate: false,
      };
    }

    case "EDIT_CURRENT_ANNOTATION_DATA": {
      const data = action.payload;
      const filteredAnnoData = state.annotationData.filter(
        (data) => data.pk !== action.pk
      );
      return {
        ...state,
        annotationData: [...filteredAnnoData, data],
      };
    }

    case "PUBLIC_ANNOTATION_DATA": {
      const data = action.payload;
      const updatedAnnoData = state.annotationData.map((dataSet) => {
        if (data.user_id === dataSet.user_id) {
          return {
            ...dataSet,
            is_public: data.status,
          };
        }
        return dataSet;
      });
      return {
        ...state,
        annotationData: updatedAnnoData,
      };
    }

    case "TOGGLE_ANNOTATION_DATA": {
      const payloadData = action.payload;
      return {
        ...state,
        annotationData: state.annotationData.map((data) => {
          if (
            payloadData.annotationIds &&
            payloadData.annotationIds.length > 0 &&
            payloadData.annotationIds.includes(data.pk)
          ) {
            return {
              ...data,
              toggle: payloadData.onOrOff,
            };
          }
          return data;
        }),
        toogleData: payloadData,
      };
    }

    case "VALIDATE_ANNOTATION_DATA": {
      const payloadData = action.payload;
      return {
        ...state,
        annotationData: state.annotationData.map((data) => {
          if (payloadData.includes(data.pk)) {
            return {
              ...data,
              isValidate: true,
            };
          }
          return data;
        }),
      };
    }

    case "SET_ANNOTATION_TYPE": {
      const type = action.payload;
      return {
        ...state,
        annotationType: type,
      };
    }

    case "SET_ANNOTATION_COLOR": {
      const color = action.payload;
      return {
        ...state,
        annotationColor: color,
      };
    }

    case "SET_ANNOTATION_ACTIVE": {
      const status = action.payload;
      return {
        ...state,
        isAnnotationActive: status,
      };
    }

    case "SET_LIVE_SHARE_ACTIVE": {
      const status = action.payload;
      return {
        ...state,
        liveShareOn: status,
      };
    }

    case "SET_LIVE_SHARE_USER_ACTIVE": {
      const status = action.payload;
      return {
        ...state,
        userLiveShareOn: status,
      };
    }

    case "SET_GENERATE_LIVESYNC_URL": {
      return {
        ...state,
        liveSyncUrl: action.payload,
        liveSyncPopupload: action.payload ? true : !state.liveSyncPopupload,
      };
    }

    case "SET_LIVESYNC_META_DATA": {
      return {
        ...state,
        liveSyncMetaData: action.payload,
      };
    }

    case "RESET_VIEWER_ANNOTATION": {
      return {
        ...initialState,
      };
    }

    case "SET_DOWNLOAD_ANNOTATION_URL": {
      return {
        ...state,
        annotationDownloadUrl: action.payload,
      };
    }

    case "RESET_DOWNLOAD_ANNOTATION": {
      return {
        ...state,
        annotationDownloadUrl: null,
      };
    }

    case "SET_SLIDE_UNIQUE_DATA": {
      return {
        ...state,
        slideUniqueId: action.payload,
      };
    }

    case "SET_VIEWER_ACTIVITY_LOG": {
      return {
        ...state,
        viewerActivityLogId: action.payload,
      };
    }

    case "GET_VIEWER_ACTIVITY_LOG": {
      return {
        ...state,
        viewerActivityData: action.payload,
      };
    }

    case "GET_SLIDE_HEAT_MAP_DATA": {
      return {
        ...state,
        heatMapData: action.payload,
      };
    }

    case "RESET_ANNOTATION_DOWNLOAD_URL": {
      return {
        ...state,
        annotationDownloadUrl: null,
      };
    }

    case "ANNOTATION_DESCRIPTION_UPDATE": {
      const newDetails = state.annotationData.map((data) => {
        if (data.unique_id === action.payload.uniqueId) {
          return {
            ...data,
            description: action.payload.description,
          };
        } else {
          return data;
        }
      });
      return {
        ...state,
        annotationData: newDetails,
      };
    }

    case "GET_ANNOTATION_DESCRIPTION": {
      return {
        ...state,
        description: action.payload,
      };
    }

    case "SET_CURRENT_ANNOTATION_DESCRIPTION": {
      const data = action.payload;
      return {
        ...state,
        description: data,
      };
    }

    case "SET_VIEWER_DZI_DATA_ARRAY": {
      return {
        ...state,
        viewerDataArray: action.payload,
      };
    }

    case "SET_ROI": {
      return {
        ...state,
        roiData: action.payload ? [action.payload] : [],
      };
    }

    case "SET_CURRENT_ANNOTATION_DATA_FOR_HIGHLIGHT_AREAS": {
      const data = action.payload;
      return {
        ...state,
        annotationData: data,
      };
    }

    case "SET_CURRENT_ANNOTATION_DATA_FOR_RUN_ANALYSIS": {
      const data = action.payload;
      return {
        ...state,
        annotationData: action.payload,
      };
    }

    case "CLOSE_DELETE_ANNOTATION_POPUP": {
      return {
        ...state,
        annotationPopupShow: action.status,
      };
    }

    case "SET_VIEWER_LABELS": {
      return {
        ...state,
        selectedSlideLabels: action.payload,
      };
    }

    case "SET_BLOCK_IMAGES_LOADING": {
      return {
        ...state,
        blockImagesLoading: action.payload,
        blockImagesError: null,
      };
    }

    case "SET_BLOCK_IMAGES": {
      return {
        ...state,
        blockImages: action.payload.images,
        blockImageRows: action.payload.rows,
        blockImagesMeta: action.payload.resolved,
        blockImagesLoading: false,
        blockImagesError: null,
        blockImageSync: DEFAULT_BLOCK_IMAGE_SYNC,
      };
    }

    // Covers both "the backend could not resolve this slide" (a 200 with
    // success:false, which is a normal outcome) and a genuine request failure. The
    // payload is the reason code; the panel decides which reads as empty and which
    // reads as an error.
    case "SET_BLOCK_IMAGES_ERROR": {
      return {
        ...state,
        blockImages: [],
        blockImageRows: [],
        blockImagesMeta: null,
        blockImagesLoading: false,
        blockImagesError: action.payload,
        selectedBlockImageIndex: null,
        blockImageSync: DEFAULT_BLOCK_IMAGE_SYNC,
      };
    }

    case "SELECT_BLOCK_IMAGE": {
      return {
        ...state,
        selectedBlockImageIndex: action.payload,
        blockImageSync: syncAfterSelecting(state, action.payload),
      };
    }

    // payload is +1 (next) or -1 (previous). Wrapping lives here rather than in the
    // arrow buttons so both callers cannot drift, and so the panel never has to know
    // how many images there are.
    case "STEP_BLOCK_IMAGE": {
      // `|| []` guards a rehydrated session persisted before this key existed —
      // redux-persist replaces the slice rather than merging into it (store.js).
      const total = (state.blockImages || []).length;
      if (!total || state.selectedBlockImageIndex === null) {
        return state;
      }
      const nextIndex = (state.selectedBlockImageIndex + action.payload + total) % total;
      return {
        ...state,
        selectedBlockImageIndex: nextIndex,
        blockImageSync: syncAfterSelecting(state, nextIndex),
      };
    }

    case "CLEAR_BLOCK_IMAGE_SELECTION": {
      return {
        ...state,
        selectedBlockImageIndex: null,
        blockImageSync: DEFAULT_BLOCK_IMAGE_SYNC,
      };
    }

    // Used when the viewer switches to a different slide — the previous slide's
    // block images must not linger behind the new one.
    case "RESET_BLOCK_IMAGES": {
      return {
        ...state,
        blockImages: [],
        blockImageRows: [],
        blockImagesMeta: null,
        blockImagesLoading: false,
        blockImagesError: null,
        selectedBlockImageIndex: null,
        blockImageSync: DEFAULT_BLOCK_IMAGE_SYNC,
      };
    }

    /* ---- Synced view ---------------------------------------------------- */

    case "SYNC_SET_SYNCED": {
      // payload: { synced, transform?, transformSource? }. Turning sync on always
      // leaves point mode; turning it off keeps a landmark fit so the user can
      // re-sync without clicking the points again, but drops an anchor transform.
      const sync = syncOf(state);
      const { synced, transform, transformSource } = action.payload;
      return {
        ...state,
        blockImageSync: synced
          ? { ...sync, synced: true, pointMode: false, transform, transformSource }
          : {
              ...sync,
              synced: false,
              ...(sync.transformSource === "landmarks"
                ? {}
                : { transform: null, transformSource: null, residualUm: null, scaleCheck: null }),
            },
      };
    }

    // payload: { transform, transformSource, residualUm, scaleCheck } or null to clear.
    case "SYNC_SET_TRANSFORM": {
      const payload = action.payload || {};
      return {
        ...state,
        blockImageSync: {
          ...syncOf(state),
          transform: payload.transform || null,
          transformSource: payload.transformSource || null,
          residualUm: payload.residualUm ?? null,
          scaleCheck: payload.scaleCheck || null,
        },
      };
    }

    case "SYNC_SET_POINT_MODE": {
      return {
        ...state,
        blockImageSync: { ...syncOf(state), pointMode: !!action.payload },
      };
    }

    // payload: { pane: "wsi" | "ss", point: { x, y } } in that pane's image pixels.
    // A pane may run at most one point ahead of the other, so points always pair up
    // in click order.
    case "SYNC_ADD_LANDMARK": {
      const sync = syncOf(state);
      const { pane, point } = action.payload;
      const other = pane === "wsi" ? "ss" : "wsi";
      if (sync.landmarks[pane].length > sync.landmarks[other].length) {
        return state;
      }
      return {
        ...state,
        blockImageSync: {
          ...sync,
          landmarks: { ...sync.landmarks, [pane]: [...sync.landmarks[pane], point] },
        },
      };
    }

    // Removes the most recently added point (from whichever pane is ahead, or the
    // SectionStar side when they are level, since pairs normally finish there).
    case "SYNC_UNDO_LANDMARK": {
      const sync = syncOf(state);
      const { wsi, ss } = sync.landmarks;
      if (!wsi.length && !ss.length) {
        return state;
      }
      const pane = wsi.length > ss.length ? "wsi" : "ss";
      return {
        ...state,
        blockImageSync: {
          ...sync,
          landmarks: { ...sync.landmarks, [pane]: sync.landmarks[pane].slice(0, -1) },
        },
      };
    }

    case "SYNC_CLEAR_LANDMARKS": {
      return {
        ...state,
        blockImageSync: { ...syncOf(state), landmarks: { wsi: [], ss: [] } },
      };
    }

    case "SYNC_RESET": {
      return {
        ...state,
        blockImageSync: DEFAULT_BLOCK_IMAGE_SYNC,
      };
    }

    default:
      return state;
  }
};
